import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useUpdatePreferences } from '@/hooks/use-preferences';
import { isAuthenticated } from '@/lib/auth';

type Props = {
  compact?: boolean;
  variant?: 'default' | 'inverse';
};

export function LanguageSwitcher({ compact = false, variant = 'default' }: Props) {
  const { i18n, t } = useTranslation('common');
  const updatePreferences = useUpdatePreferences();

  const current = i18n.language?.split('-')[0] as 'de' | 'en' | 'tr' | undefined;
  const value = (current && ['de', 'en', 'tr'].includes(current) ? current : 'en') as 'de' | 'en' | 'tr';
  const items = [
    { value: 'en' as const, label: t('languages.en') },
    { value: 'de' as const, label: t('languages.de') },
    { value: 'tr' as const, label: t('languages.tr') },
  ];

  const handleLanguageChange = (lng: 'de' | 'en' | 'tr') => {
    void i18n.changeLanguage(lng);
    if (isAuthenticated()) {
      updatePreferences.mutate({ preferredLanguage: lng });
    }
  };

  return (
    <div className={compact ? '' : 'space-y-1'}>
      {!compact && (
        <div className="text-xs text-slate-500">{t('language')}</div>
      )}
      <Select value={value} onValueChange={handleLanguageChange}>
        <SelectTrigger
          className={cn(
            compact ? 'h-7' : '',
            variant === 'inverse'
              ? 'bg-transparent text-slate-100 border-slate-600 focus:ring-0 focus:ring-offset-0 hover:bg-slate-700/30'
              : ''
          )}
        >
          <SelectValue placeholder="Language" />
        </SelectTrigger>
        <SelectContent className={variant === 'inverse' ? 'bg-slate-800 text-slate-100 border-slate-700' : ''}>
          {items.map((it) => (
            <SelectItem key={it.value} value={it.value} className={variant === 'inverse' ? 'focus:bg-slate-700 focus:text-white' : ''}>
              {it.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default LanguageSwitcher;
